import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Col,
  Container,
  Row,
  Button,
  FormGroup,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { ReactstrapInput } from "reactstrap-formik";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import * as Yup from "yup";
import moment from "moment";

import FormHelpers from "../../helpers/FormHelpers";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import OptGroupSelectDropDown from "./Fields/OptGroupSelectDropDown";

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";
import { fetchOccasionData } from "../../store/card-preview-actions";
import { sendCartData } from "../../store/configurator-actions";

function RequestForm() {
  const dispatch = useDispatch();

  const addingToCart = useSelector((state) => state.configurator.AddingToCart);
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  const currentProductState =
    currentRequest.Products[currentRequest.CurrentProduct];
  const currentPreviewState = useSelector((state) => state.cardPreview);

  const isUpdating = requestDetails.Id;

  // evaluate mass type
  const isIndividual = requestDetails.MassTypeValue == "individual";
  const isGregorian = requestDetails.MassTypeValue == "gregorian";

  // prepare occasions list
  const occasions = currentRequest.Lists.Occasions.MassRequestOccasions.filter(
    (item) => item.Parent !== null
  )
    .map((item) => {
      return {
        id: item.Id,
        name: item.Description,
        optionGroup: item.Parent.Description,
      };
    })
    .reduce((acc, option) => {
      const accOptionGroup = acc[option.optionGroup] || [];

      return {
        ...acc,
        [option.optionGroup]: [...accOptionGroup, option],
      };
    }, {});

  const sortedOccasions = Object.keys(occasions)
    .sort()
    .reduce((obj, key) => {
      obj[key] = occasions[key];
      return obj;
    }, {});

  // console.log(currentRequest.ServerDateTime);

  // Use ServerDateTime as the base for minDateSelect
  const minDateSelect = moment(currentRequest.ServerDateTime).add(7, "days");
  const maxDateSelect = moment(currentRequest.ServerDateTime).add(1, "year");
  const closedDates = currentRequest.Lists.ClosedDates;
  const closedDatesArray = closedDates.map((item) => {
    return moment(item.ClosedDate).toDate();
  });

  // get default offering amount based on product
  let defaultOfferingAmount = Number(
    currentRequest.Products[currentRequest.CurrentProduct].SKUPrice
  );

  let defaultIntentionConfig = {
    placeholder: "Up to 255 Characters",
    rows: 8,
    cols: 32,
    max: 256,
  };
  if (currentRequest.DonorRequest.CardTypeValue !== "nocard" || isGregorian) {
    defaultIntentionConfig = {
      placeholder: "2 lines of 32 characters each",
      rows: 2,
      cols: 32,
      max: 64,
    };
  }

  // init state for form fields
  const [massQuantity, setMassQuantity] = useState(1);
  const [intentionConfig, setIntentionConfig] = useState(
    defaultIntentionConfig
  );

  const formRef = useRef(null);
  const intentionRef = useRef(null);
  const massDateRef = useRef(null);
  const dateOfDeathRef = useRef(null);

  const openMassDatePicker = () => massDateRef.current.setOpen(true);
  const openDateOfDeathDatePicker = () => dateOfDeathRef.current.setOpen(true);

  const validationRules = {
    intention: Yup.string().required("Required"),
    cardType: Yup.string().required("Required"),
    massQuantity: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    occasion: Yup.string().required("Required"),
    massDate: Yup.string().when("massQuantity", (massQuantity) => {
      if (massQuantity > 5) return Yup.string().required("Required");
    }),
  };

  if (isGregorian) {
    validationRules.deceasedRelationship = Yup.string().when(
      "cardType",
      (cardType) => {
        if (cardType !== "sendtome") return Yup.string().required("Required");
      }
    );
    validationRules.massDate = Yup.string().required("Required");
  }

  const formSchema = Yup.object().shape(validationRules);

  // add to cart if no card
  useEffect(() => {
    if (addingToCart) {
      let currentCart = { ...requestDetails };
      let currentPreview = { ...currentPreviewState };
      let currentProduct = { ...currentProductState };
      dispatch(sendCartData(currentCart, currentPreview, currentProduct));

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [dispatch, addingToCart]);

  const [showOfferingTime, setShowOfferingTime] = useState(true);
  const [showMassDateError, setShowMassDateError] = useState(false);
  const [nextClosedDate, setNextClosedDate] = useState(null);
  const toggleMassDateErrorModal = (e) => {
    setShowMassDateError(!showMassDateError);
  };

  let massDate = "";

  function formSubmitHandler(values) {
    let valid = true;
    let massQuantity = values.massQuantity;
    const selectedMassType = requestDetails.MassTypeValue;

    if (selectedMassType == "novena") {
      massQuantity = 9;
      values.offeringTime = "consecutivedays";
      setShowOfferingTime(false);
    }
    if (selectedMassType == "gregorian") {
      massQuantity = 30;
      values.offeringTime = "consecutivedays";
      setShowOfferingTime(false);
    }
    let offeringTime = values.offeringTime;
    if (offeringTime == "consecutivedays") {
      let closedDate = null;
      let nextClosedDate = null;
      let now = moment();
      let selectedDate = moment(values.massDate);
      let seriesEndDate = moment(values.massDate);
      seriesEndDate.add(massQuantity - 1, "days");

      // Create a filtered copy for validation that excludes HolyWeek dates when isIndividual is true
      const closedDatesForValidation = closedDates.filter((item) => {
        if (isIndividual && item.HolyWeek === "1") {
          return false;
        }
        return true;
      });

      if (closedDatesForValidation.length > 0) {
        for (let dt in closedDatesForValidation) {
          closedDate = moment(closedDatesForValidation[dt].ClosedDate);
          if (closedDate >= selectedDate && seriesEndDate >= closedDate) {
            if (
              selectedMassType != "gregorian" ||
              (selectedMassType == "gregorian" &&
                closedDatesForValidation[dt].HolyWeek != "1")
            ) {
              valid = false;
              break;
            }
          }
        }
      }

      if (!valid) {
        let validClosedDate = null;
        for (let dt in closedDatesForValidation) {
          nextClosedDate = moment(closedDatesForValidation[dt].ClosedDate);
          if (
            nextClosedDate >= selectedDate &&
            Number(closedDatesForValidation[dt].DaysToNextClosedDate) > massQuantity
          ) {
            validClosedDate = closedDatesForValidation[dt];
            break;
          }
        }
        nextClosedDate.add(1, "days");
        setNextClosedDate(nextClosedDate.format("MM/DD/YYYY"));
        setShowMassDateError(true);
      }
    }

    if (valid) {
      dispatch(configuratorActions.setRequest(values));
      dispatch(cardPreviewActions.setFieldFocus(""));
      FormHelpers.scrollToTop();
    }
  }

  const setIntention = (cardTypeValue) => {
    if (cardTypeValue === "nocard" && !isGregorian) {
      setIntentionConfig({
        placeholder: "Up to 255 Characters",
        rows: 8,
        cols: 32,
        max: 256,
      });
    } else {
      setIntentionConfig({
        placeholder: "2 lines of 32 characters each",
        rows: 2,
        cols: 32,
        max: 64,
      });
    }
    dispatch(configuratorActions.setCardType(cardTypeValue));
  };

  useEffect(() => {
    if (intentionRef.current !== null) {
      const maxLength = Number(intentionConfig.max);
      const inputLength = intentionRef.current.props.value.length;
      const intention = formRef.current.values.intention;
      if (inputLength > maxLength) {
        formRef.current.setFieldValue(
          "intention",
          intention.substring(0, maxLength)
        );
        dispatch(
          cardPreviewActions.setFieldValue({
            key: "CardIntention",
            value: intention.substring(0, maxLength),
          })
        );
      }
    }
  }, [intentionConfig]);

  const cardTypeClickHandler = (event) => {
    setIntention(event.target.value);
  };

  const offeringTimeClickHandler = (event) => {
    dispatch(configuratorActions.setOfferingTime(event.target.value));
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(e.target.name));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const setOfferingAmount = (value) => {
    dispatch(
      configuratorActions.setOfferingAmount(value * defaultOfferingAmount)
    );
  };

  const occasionSelectHandler = (occasionId) => {
    dispatch(cardPreviewActions.setOccasionLoading());
    dispatch(fetchOccasionData(occasionId));
  };

  if (requestDetails.MassDate) {
    massDate = moment(requestDetails.MassDate).valueOf();
  }

  let dateOfDeath = "";
  if (requestDetails.DateOfDeath) {
    dateOfDeath = moment(requestDetails.DateOfDeath).valueOf();
  }

  const offeringAmount = defaultOfferingAmount * requestDetails.MassQuantity;

  return (
    <div className="cfg-input-form cfg-request" data-step="request">
      <Formik
        initialValues={{
          intention: requestDetails.Intention,
          cardType: requestDetails.CardTypeValue,
          massQuantity: requestDetails.MassQuantity,
          occasion: requestDetails.Occasion,
          dateOfDeath: dateOfDeath,
          deceasedRelationship: requestDetails.DeceasedRelationship,
          massDate: massDate,
          offeringTime: isIndividual
            ? requestDetails.OfferingTime
            : "consecutivedays",
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              {!isGregorian && (
                <Row className="mb-2">
                  <Col xs="12">
                    <InfoTooltip
                      id="occasionHelp"
                      title="Help"
                      text="Click the down arrow to select the option that best describes the reason for your request."
                    />
                    <h4 className="required">Select an Occasion:</h4>
                    <FormGroup>
                      <OptGroupSelectDropDown
                        name="occasion"
                        error={errors.occasion}
                        required={true}
                        emptyOption="Select..."
                        options={sortedOccasions}
                        defaultValue={values.occasion}
                        onChange={(e) => {
                          handleChange(e);
                          occasionSelectHandler(e.currentTarget.value);
                        }}
                        className={`form-control ${
                          errors.occasion && touched.occasion
                            ? "is-invalid"
                            : ""
                        }`}
                        onBlur={handleBlur}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}
              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="intentionHelp"
                    title="Help"
                    text="The person(s) on whose behalf the Mass is to be celebrated."
                  />
                  <h4 className="required">Name of person(s):</h4>
                  <FormGroup>
                    <Input
                      name="intention"
                      type="textarea"
                      id="intention"
                      ref={intentionRef}
                      datamax={intentionConfig.max}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      style={{ height: "60px" }}
                      value={values.intention}
                      rows={intentionConfig.rows}
                      cols={intentionConfig.cols}
                      className="cfg-intention-input parse-new-lines"
                      placeholder={intentionConfig.placeholder}
                      onKeyUp={FormHelpers.handleMultilineKeyUp}
                      onKeyDown={FormHelpers.handleMultilineKeyDown}
                      onPaste={FormHelpers.handleMultilineUpdate}
                      onChange={(e) => {
                        FormHelpers.handleMultilineUpdate(e);
                        handleChange(e);
                        dispatch(
                          cardPreviewActions.setFieldValue({
                            key: "CardIntention",
                            value: e.target.value,
                          })
                        );
                      }}
                      onFocus={(e) => {
                        inputFocusHandler(e);
                      }}
                      onBlur={(e) => {
                        FormHelpers.handleMultilineUpdate(e);
                        FormHelpers.cleanupEmptyLines(e, values, "intention");
                        handleBlur(e);
                        inputBlurHandler();
                      }}
                      invalid={Boolean(
                        touched["intention"] && errors["intention"]
                      )}
                    />
                    {touched["intention"] && errors["intention"] ? (
                      <FormFeedback>{errors["intention"]}</FormFeedback>
                    ) : (
                      ""
                    )}
                  </FormGroup>
                </Col>
                <Col xs="12">
                  <h4 className="required">Tell Us If You Need A Card:</h4>
                  <FormGroup>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="nocard"
                          onClick={cardTypeClickHandler}
                        />
                        I do not need a card
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtosomeone"
                          onClick={cardTypeClickHandler}
                        />
                        Mail the card to someone for me
                      </label>
                    </div>
                    <div className="form-check">
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="sendtome"
                          onClick={cardTypeClickHandler}
                        />
                        Please send the card to me
                      </label>
                    </div>
                    <div
                      className={`form-check ${
                        errors.cardType && touched.cardType ? "is-invalid" : ""
                      }`}
                    >
                      <label>
                        <Field
                          type="radio"
                          name="cardType"
                          value="ecard"
                          onClick={cardTypeClickHandler}
                        />
                        Send an eCard for me
                      </label>
                    </div>
                    {errors.cardType && touched.cardType && (
                      <div className="invalid-feedback">{errors.cardType}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              {isIndividual && (
                <Row>
                  <Col xs="12">
                    <InfoTooltip
                      id="massQuantityHelp"
                      title="Help"
                      text="Enter the number of individual Masses you wish to have celebrated."
                    />
                    <h4 className="required">
                      Tell Us How Many Masses You Are Requesting:
                    </h4>
                  </Col>
                </Row>
              )}

              <Row>
                {isIndividual && (
                  <Col xs="6">
                    <FormGroup>
                      <label className="required" htmlFor="massQuantity">
                        Number of Masses:
                      </label>
                      <Field
                        name="massQuantity"
                        id="massQuantity"
                        component={ReactstrapInput}
                        onKeyUp={(e) => {
                          handleChange(e);
                          let currentValue = e.currentTarget.value;
                          if (!errors.massQuantity) {
                            setMassQuantity(currentValue);
                            setOfferingAmount(currentValue);
                            dispatch(
                              configuratorActions.setMassQuantity(currentValue)
                            );
                            dispatch(
                              cardPreviewActions.setMassQuantity(currentValue)
                            );
                          }
                        }}
                      />
                    </FormGroup>
                  </Col>
                )}

                <Col xs="6">
                  <label>Offering Amount:</label>$
                  {Number(offeringAmount).toFixed(2)}
                </Col>
              </Row>

              {isGregorian && (
                <Row className="mb-2">
                  <Col xs="12">
                    <InfoTooltip
                      id="dateOfDeathHelp"
                      title="Help"
                      text="Please enter the date your loved one or friend passed away."
                    />
                    <label>Date of Death:</label>
                    <FormGroup>
                      <InputGroup>
                        <InputGroupText>
                          <i
                            className="fas fa-calendar-alt"
                            onClick={openDateOfDeathDatePicker}
                          ></i>
                        </InputGroupText>
                        <DatePicker
                          id="dateOfDeath"
                          name="dateOfDeath"
                          ref={dateOfDeathRef}
                          className={`form-control ${
                            errors.dateOfDeath && touched.dateOfDeath
                              ? "is-invalid"
                              : ""
                          }`}
                          onKeyDown={(e) => e.preventDefault()}
                          dateFormat="MM/dd/yyyy"
                          value={values.dateOfDeath}
                          selected={values.dateOfDeath}
                          onChange={(date, e) => {
                            handleChange(e);
                            if (date !== null) {
                              setFieldValue("dateOfDeath", date);
                            }
                          }}
                          onChangeRaw={(e) => {
                            setFieldTouched("dateOfDeath", true, true);
                          }}
                          onBlur={handleBlur}
                          shouldCloseOnSelect={true}
                          maxDate={moment().toDate()}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          autoComplete="off"
                        />
                        <button
                          onClick={(e) => {
                            setFieldValue("dateOfDeath", "");
                          }}
                          type="button"
                          className="react-datepicker__close-icon"
                          aria-label="Close"
                          tabIndex="-1"
                        ></button>
                      </InputGroup>
                      <div
                        className={`${
                          errors.dateOfDeath && touched.dateOfDeath
                            ? "is-invalid"
                            : ""
                        }`}
                      ></div>
                      {errors.dateOfDeath && touched.dateOfDeath && (
                        <div className="invalid-feedback">
                          {errors.dateOfDeath}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              )}
              {isGregorian && (
                <Row className="mb-2">
                  <Col xs="12">
                    <InfoTooltip
                      id="deceasedRelationshipHelp"
                      title="Help"
                      text="Please select your relationship to the deceased. Select “Other” if it is not in the list."
                    />
                    <FormGroup>
                      <label
                        className={
                          values.cardType !== "sendtome" ? "required" : ""
                        }
                      >
                        The Deceased is my:
                      </label>
                      <select
                        name="deceasedRelationship"
                        value={values.deceasedRelationship}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-control ${
                          errors.deceasedRelationship &&
                          touched.deceasedRelationship
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Please select...</option>
                        <option>Brother/Sister</option>
                        <option>Grandparent</option>
                        <option>Grandson/Granddaughter</option>
                        <option>Nephew/Niece</option>
                        <option>Parent</option>
                        <option>Son/Daughter</option>
                        <option>Spouse</option>
                        <option>Uncle/Aunt</option>
                        <option>Other</option>
                      </select>
                      <div
                        className={`${
                          errors.deceasedRelationship &&
                          touched.deceasedRelationship
                            ? "is-invalid"
                            : ""
                        }`}
                      ></div>
                      {errors.deceasedRelationship &&
                        touched.deceasedRelationship && (
                          <div className="invalid-feedback">
                            {errors.deceasedRelationship}
                          </div>
                        )}
                    </FormGroup>
                  </Col>
                </Row>
              )}

              <Row>
                <Col xs="12">
                  <InfoTooltip
                    id="massDateHelp"
                    title="Help"
                    text="The Mass(es) will be offered or begin on the date chosen."
                  />
                  <h4
                    className={
                      massQuantity > 5 || isGregorian ? "required" : ""
                    }
                  >
                    {isIndividual && (
                      <span>
                        The Mass(es) will be offered as early as possible or on:
                      </span>
                    )}
                    {!isIndividual && <span>The Masses will begin on:</span>}
                  </h4>

                  <FormGroup>
                    <InputGroup>
                      <InputGroupText>
                        <i
                          className="fas fa-calendar-alt"
                          onClick={openMassDatePicker}
                        ></i>
                      </InputGroupText>
                      <DatePicker
                        id="massDate"
                        name="massDate"
                        ref={massDateRef}
                        className={`form-control ${
                          errors.massDate && touched.massDate
                            ? "is-invalid"
                            : ""
                        }`}
                        onKeyDown={(e) => e.preventDefault()}
                        dateFormat="MM/dd/yyyy"
                        value={values.massDate}
                        selected={values.massDate}
                        onChange={(date, e) => {
                          handleChange(e);
                          if (date !== null) {
                            setFieldValue("massDate", date);
                            dispatch(
                              cardPreviewActions.setFieldValue({
                                key: "CardMassDate",
                                value: moment(date).format("MMMM D, YYYY"),
                              })
                            );
                          }
                        }}
                        onChangeRaw={(e) => {
                          setFieldTouched("massDate", true, true);
                        }}
                        onBlur={(e) => {
                          handleBlur(e);
                          inputBlurHandler();
                        }}
                        onFocus={inputFocusHandler}
                        shouldCloseOnSelect={true}
                        minDate={minDateSelect.toDate()}
                        maxDate={maxDateSelect.toDate()}
                        excludeDates={closedDatesArray}
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        autoComplete="off"
                      />
                      <button
                        onClick={(e) => {
                          setFieldValue("massDate", "");
                        }}
                        type="button"
                        className="react-datepicker__close-icon"
                        aria-label="Close"
                        tabIndex="-1"
                      ></button>
                    </InputGroup>

                    <div
                      className={`${
                        errors.massDate && touched.massDate ? "is-invalid" : ""
                      }`}
                    ></div>
                    {errors.massDate && touched.massDate && (
                      <div className="invalid-feedback">{errors.massDate}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              {isIndividual &&
                showOfferingTime &&
                values.massDate !== "" &&
                values.massQuantity > 1 &&
                values.massQuantity <= 4 && (
                  <Row>
                    <Col xs="12">
                      <FormGroup>
                        <div className="form-check">
                          <label>
                            <Field
                              type="radio"
                              name="offeringTime"
                              value="sameday"
                              onClick={offeringTimeClickHandler}
                            />
                            On the same day
                          </label>
                        </div>
                        <div className="form-check">
                          <label>
                            <Field
                              type="radio"
                              name="offeringTime"
                              value="consecutivedays"
                              onClick={offeringTimeClickHandler}
                            />
                            On consecutive days
                          </label>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                )}
              {/* <DebugOutput
                errors={errors}
                values={FormHelpers.getValues(values)}
              /> */}
            </Container>
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="float-end"
            >
              {requestDetails.CardTypeValue === "nocard" &&
                (isUpdating ? "Update Cart" : "Add to Cart")}

              {requestDetails.CardTypeValue !== "nocard" && "Next"}
            </Button>
            
            <Modal
              isOpen={showMassDateError}
              toggle={toggleMassDateErrorModal}
              size="sm"
            >
              <ModalHeader toggle={toggleMassDateErrorModal} close={() => null}>
                Your Requested Mass Date
              </ModalHeader>
              <ModalBody>
                <Container>
                  <Row>
                    <Col>
                      <p>
                        All priests are scheduled for one or more days. This series
                        may begin {nextClosedDate}. Please click OK to choose another date.
                      </p>
                    </Col>
                  </Row>
                </Container>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="secondary"
                  onClick={() => {
                    formRef.current.errors.massDate = "Please select another date. The next available date is " + nextClosedDate + ".";
                    
                    setShowMassDateError(false);
                  }}
                >
                  OK
                </Button>
              </ModalFooter>
            </Modal>
          </Form>
        )}

        
      </Formik>
    </div>
  );
}

export default RequestForm;
